/**
 * QUIPWA — SqlJsAdapter
 *
 * SQLite (sql.js, in memory) with optional write-behind persistence to
 * IndexedDB.
 *
 *   persistence: 'indexeddb'  → the database survives reloads. Writes mark it
 *                               dirty; a debounced flush (default 400 ms after
 *                               the last write, but at least every 2 s while
 *                               writes keep coming) serializes it to
 *                               IndexedDB. Leaving the page (visibilitychange
 *                               → hidden, pagehide) hands the latest snapshot
 *                               to IndexedDB before the handler returns.
 *   persistence: 'memory'     → never touches IndexedDB. For throwaway copies,
 *                               e.g. a `_state.db` downloaded from Drive to
 *                               merge.
 *
 * What changed and why (docs/modulos/almacenamiento.md):
 * - A reload right after writing lost everything since the last flush (500
 *   notes, measured on 08-10-2026). Not because the flush was slow — 2.6 MB
 *   serialize and write in ~4 ms — but because the write started on
 *   `pagehide` was never committed: IndexedDB auto-commit needs the page to
 *   live until the request succeeds. The store now commits explicitly, the
 *   hide flush starts synchronously and no longer waits for a write still in
 *   flight (whose completion a dying page never hears about), and a steady
 *   stream of writes can no longer postpone the flush indefinitely.
 * - It used to export the whole database every 30 s whether or not anything
 *   changed, and nothing on page hide. Closing the PWA mid-session lost up to
 *   30 s of reviews; idle devices burned battery re-serializing.
 * - Remote copies were opened under the *same* IndexedDB key as the real
 *   database, so importing one overwrote the user's persisted local data.
 *   Memory mode makes that impossible by construction.
 * - Transactions are serialized: two concurrent `transaction()` calls used to
 *   interleave their statements inside one BEGIN…COMMIT.
 */

import type { Database } from 'sql.js'
import type { IDBAdapter, SQLParam } from './db-adapter.ts'
import { getSqlJsSingleton } from './sqljs-loader.ts'
import { defaultBlobStore, type IdbBlobStore } from './idb-blob-store.ts'

export type Persistence = 'indexeddb' | 'memory'

export interface SqlJsAdapterOptions {
  persistence?: Persistence
  /** Debounce between the last write and the flush to IndexedDB. */
  flushDelayMs?: number
  /**
   * Longest a change waits for its flush while writes keep coming (each write
   * restarts the debounce). Bounds what a crash, which fires no `pagehide`,
   * can take with it during a long burst such as an import.
   */
  maxFlushDelayMs?: number
  /**
   * Where lifecycle events (`pagehide`, `beforeunload`) come from; `null`
   * disables automatic flush on hide.
   */
  lifecycleTarget?: Pick<Window, 'addEventListener' | 'removeEventListener'> | null
  /** Visibility source for `visibilitychange`; defaults to `document`. */
  visibilitySource?: Pick<Document, 'addEventListener' | 'removeEventListener' | 'visibilityState'> | null
  store?: IdbBlobStore
}

export type ChangeListener = (changeCount: number) => void

const DEFAULT_FLUSH_DELAY_MS = 400
const DEFAULT_MAX_FLUSH_DELAY_MS = 2000

/** Statements that cannot modify the database (skip dirty-marking for them). */
const READ_ONLY = /^\s*(SELECT|EXPLAIN|PRAGMA\s+(table_info|index_list|user_version\s*$|foreign_key_list))/i

export class SqlJsAdapter implements IDBAdapter {
  readonly name: string
  readonly persistence: Persistence
  private readonly key: string
  private readonly flushDelayMs: number
  private readonly maxFlushDelayMs: number
  private readonly store: IdbBlobStore
  private readonly lifecycleTarget: SqlJsAdapterOptions['lifecycleTarget']
  private readonly visibilitySource: SqlJsAdapterOptions['visibilitySource']

  private db: Database | null = null
  /** Changes not yet handed to IndexedDB. */
  private dirty = false
  /** When the oldest of those changes was made (for `maxFlushDelayMs`). */
  private dirtySince = 0
  private flushTimer: ReturnType<typeof setTimeout> | null = null
  /** The latest write handed to IndexedDB, until it settles. */
  private writing: Promise<void> | null = null
  private writesInFlight = 0
  private writeSeq = 0
  private settledSeq = 0
  /** On while the most recent write that settled failed: the data is not on disk. */
  private guardingUnload = false
  private inTransaction = false
  private changes = 0
  private readonly listeners = new Set<ChangeListener>()

  /** Serializes top-level transactions. */
  private txQueue: Promise<void> = Promise.resolve()
  private savepointSeq = 0

  constructor(name: string, options: SqlJsAdapterOptions = {}) {
    this.name = name
    this.key = `QUIPWA:${name}`
    this.persistence = options.persistence ?? 'indexeddb'
    this.flushDelayMs = options.flushDelayMs ?? DEFAULT_FLUSH_DELAY_MS
    this.maxFlushDelayMs = Math.max(this.flushDelayMs, options.maxFlushDelayMs ?? DEFAULT_MAX_FLUSH_DELAY_MS)
    this.store = options.store ?? defaultBlobStore
    this.lifecycleTarget = options.lifecycleTarget !== undefined
      ? options.lifecycleTarget
      : typeof window === 'undefined' ? null : window
    this.visibilitySource = options.visibilitySource !== undefined
      ? options.visibilitySource
      : typeof document === 'undefined' ? null : document
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────

  async initialize(schema: string): Promise<void> {
    const SQL = await getSqlJsSingleton()
    const existing = this.persistence === 'indexeddb' ? await this.store.get(this.key) : null
    this.db = existing ? new SQL.Database(new Uint8Array(existing)) : new SQL.Database()
    if (schema.trim()) this.db.run(schema)

    if (this.persistence === 'indexeddb') {
      this.lifecycleTarget?.addEventListener('pagehide', this.onPageHide)
      this.visibilitySource?.addEventListener('visibilitychange', this.onVisibilityChange)
    }
  }

  async close(): Promise<void> {
    this.lifecycleTarget?.removeEventListener('pagehide', this.onPageHide)
    this.visibilitySource?.removeEventListener('visibilitychange', this.onVisibilityChange)
    await this.flushNow()
    this.setUnloadGuard(false)
    this.cancelTimer()
    this.db?.close()
    this.db = null
    this.listeners.clear()
  }

  get isOpen(): boolean {
    return this.db !== null
  }

  // ── Queries ─────────────────────────────────────────────────────────────

  async query<T>(sql: string, params: SQLParam[] = []): Promise<T[]> {
    const db = this.assertOpen()
    const stmt = db.prepare(sql)
    try {
      stmt.bind(params)
      const rows: T[] = []
      while (stmt.step()) rows.push(stmt.getAsObject() as T)
      return rows
    } finally {
      stmt.free()
    }
  }

  async queryOne<T>(sql: string, params: SQLParam[] = []): Promise<T | null> {
    const rows = await this.query<T>(sql, params)
    return rows[0] ?? null
  }

  async run(sql: string, params: SQLParam[] = []): Promise<void> {
    const db = this.assertOpen()
    if (params.length > 0) db.run(sql, params)
    else db.run(sql)
    if (!READ_ONLY.test(sql)) this.markChanged()
  }

  async transaction(fn: (db: IDBAdapter) => Promise<void>): Promise<void> {
    // Wait for any transaction already running, then hold the queue.
    const previous = this.txQueue
    let release!: () => void
    this.txQueue = new Promise<void>((resolve) => { release = resolve })
    await previous

    const db = this.assertOpen()
    db.run('BEGIN')
    this.inTransaction = true
    try {
      await fn(this.nestedView())
      db.run('COMMIT')
      this.markChanged()
    } catch (err) {
      try { db.run('ROLLBACK') } catch { /* already rolled back by SQLite */ }
      throw err
    } finally {
      this.inTransaction = false
      release()
    }
  }

  // ── Persistence ─────────────────────────────────────────────────────────

  /** Monotonic count of committed changes since this adapter was opened. */
  get changeCount(): number {
    return this.changes
  }

  /** Subscribe to committed changes. Returns an unsubscribe function. */
  onChange(listener: ChangeListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  get hasPendingWrites(): boolean {
    return this.dirty || this.writesInFlight > 0
  }

  /** Persist now if there is anything to persist; resolves once it is durable. */
  async flushNow(): Promise<void> {
    this.cancelTimer()
    if (this.persistence !== 'indexeddb') {
      this.dirty = false
      return
    }
    // sql.js export() closes and reopens the database, which would silently
    // roll back a transaction that is still running — so wait for it first.
    await this.txQueue
    for (;;) {
      if (this.inTransaction) {
        await this.txQueue
        continue
      }
      // Wait out a write already in progress; it may have started before the
      // latest change, so loop until nothing is dirty.
      if (this.writing) {
        await this.writing
        continue
      }
      if (!this.dirty || !this.db) return
      await this.startWrite()
    }
  }

  /** Serialize the database. Waits for a running transaction to finish first. */
  async exportToArrayBuffer(): Promise<ArrayBuffer> {
    this.assertOpen()
    await this.txQueue
    return this.serialize()
  }

  async importFromArrayBuffer(buffer: ArrayBuffer): Promise<void> {
    const SQL = await getSqlJsSingleton()
    this.db?.close()
    this.db = new SQL.Database(new Uint8Array(buffer))
    this.markChanged()
    if (this.persistence === 'indexeddb') await this.flushNow()
  }

  // ── internals ───────────────────────────────────────────────────────────

  private serialize(): ArrayBuffer {
    const data = this.assertOpen().export()
    return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer
  }

  private markChanged(): void {
    this.changes += 1
    for (const listener of this.listeners) listener(this.changes)
    if (this.persistence !== 'indexeddb') return
    const now = Date.now()
    if (!this.dirty) {
      this.dirty = true
      this.dirtySince = now
    }
    // Debounce, but never past maxFlushDelayMs from the oldest unsaved change:
    // creating 500 notes back to back used to keep everything in memory only
    // for the 45 s it took.
    const delay = Math.max(0, Math.min(this.flushDelayMs, this.dirtySince + this.maxFlushDelayMs - now))
    this.cancelTimer()
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null
      this.flushNow().catch(this.logFlushError)
    }, delay)
  }

  /**
   * Serialize now and hand the snapshot to IndexedDB. The store issues the
   * write before this returns, so it works from `pagehide`. It does not wait
   * for earlier writes: IndexedDB applies writes to the same store in the
   * order they were issued, so the newest snapshot lands last.
   */
  private startWrite(): Promise<void> {
    this.cancelTimer()
    const snapshot = this.serialize()
    this.dirty = false
    const seq = ++this.writeSeq
    this.writesInFlight += 1
    const write: Promise<void> = this.store.put(this.key, snapshot).then(
      () => this.writeSettled(seq, write, false),
      (err: unknown) => {
        this.writeSettled(seq, write, true)
        throw err
      },
    )
    this.writing = write
    return write
  }

  private writeSettled(seq: number, write: Promise<void>, failed: boolean): void {
    this.writesInFlight -= 1
    if (this.writing === write) this.writing = null
    // A newer write already settled: its outcome is the one that counts.
    if (seq < this.settledSeq) return
    this.settledSeq = seq
    if (failed && !this.dirty) {
      // Keep the data marked unsaved so the next flush retries.
      this.dirty = true
      this.dirtySince = Date.now()
    }
    this.setUnloadGuard(failed)
  }

  private cancelTimer(): void {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer)
      this.flushTimer = null
    }
  }

  private readonly logFlushError = (err: unknown) => {
    console.error(`[SqlJsAdapter:${this.name}] flush failed`, err)
  }

  /**
   * Leaving the page: hand the latest changes to IndexedDB before the handler
   * returns. Anything deferred to a later task never runs once the page is
   * gone, and waiting for a write still in flight would wait forever: a
   * dying page is never told that it finished.
   */
  private writeOnLeave(): void {
    if (this.persistence !== 'indexeddb' || !this.dirty || !this.db) return
    if (this.inTransaction) {
      // export() would roll the transaction back. Flush when it ends — best
      // effort: transactions only run DB statements, so it is rare for one to
      // still be open when an event arrives.
      this.flushNow().catch(this.logFlushError)
      return
    }
    this.startWrite().catch(this.logFlushError)
  }

  private readonly onPageHide = () => {
    this.writeOnLeave()
  }

  private readonly onVisibilityChange = () => {
    if (this.visibilitySource?.visibilityState === 'hidden') this.writeOnLeave()
  }

  /**
   * Listens only while the last write failed (quota, a broken IndexedDB):
   * what is on screen is not on disk, so retry and ask the browser to
   * confirm leaving. When writes work, leaving is safe and nothing is asked.
   */
  private readonly onBeforeUnload = (event: Event) => {
    this.writeOnLeave()
    event.preventDefault() // the browser asks "Leave site?"
  }

  private setUnloadGuard(on: boolean): void {
    if (on === this.guardingUnload || !this.lifecycleTarget) return
    this.guardingUnload = on
    if (on) this.lifecycleTarget.addEventListener('beforeunload', this.onBeforeUnload)
    else this.lifecycleTarget.removeEventListener('beforeunload', this.onBeforeUnload)
  }

  /**
   * The adapter handed to a transaction body: statements run directly (no
   * queueing — the transaction already holds the queue) and nested
   * `transaction` calls become SAVEPOINTs.
   */
  private nestedView(): IDBAdapter {
    const view: IDBAdapter = {
      query: (sql, params) => this.query(sql, params),
      queryOne: (sql, params) => this.queryOne(sql, params),
      run: async (sql, params = []) => {
        const db = this.assertOpen()
        if (params.length > 0) db.run(sql, params)
        else db.run(sql)
      },
      transaction: async (fn) => {
        const db = this.assertOpen()
        const name = `sp_${++this.savepointSeq}`
        db.run(`SAVEPOINT ${name}`)
        try {
          await fn(view)
          db.run(`RELEASE ${name}`)
        } catch (err) {
          db.run(`ROLLBACK TO ${name}`)
          db.run(`RELEASE ${name}`)
          throw err
        }
      },
      initialize: () => Promise.reject(new Error('initialize() inside a transaction')),
      close: () => Promise.reject(new Error('close() inside a transaction')),
      exportToArrayBuffer: () => Promise.reject(new Error('export inside a transaction')),
      importFromArrayBuffer: () => Promise.reject(new Error('import inside a transaction')),
    }
    return view
  }

  private assertOpen(): Database {
    if (!this.db) throw new Error(`SqlJsAdapter(${this.name}): not initialized`)
    return this.db
  }
}
